import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { get, isEmpty, isNil } from 'lodash';
import { BareDropdown, PaginatedDropdown } from 'taboola-ultimate-ui';
import { FORM_MODES } from 'config/formModes';
import { ENTITY } from 'modules/campaigns/config';
import { AllEntityId } from 'modules/campaigns/config/allEntityId';
import { useCampaignGroupIdFormValidatedValue } from 'modules/campaigns/hooks/useCampaignGroupIdFormValidatedValue';
import { useLastCreatedCampaignGroupId } from 'modules/campaigns/hooks/useLastCreatedCampaignGroupId';
import { useReportEntityIds } from 'modules/campaigns/hooks/useReportEntityIds';
import { useReportEntityType } from 'modules/campaigns/hooks/useReportEntityType';
import { getBudgetInitialValuesWithoutCampaignGroup } from 'modules/campaigns/modules/common-campaign-form/config/InitialValues/campaignInitialValues';
import { withIndication } from 'modules/errors/components/withIndication';
import { FEATURE_FLAGS, useConfigMatch } from 'modules/taboola-common-frontend-modules/account-configurations';
import { useFormDataContext } from 'modules/taboola-common-frontend-modules/formData';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { campaignsGroupMapSelector } from '../../../../../campaigns-group/selectors';
import { isCampaignGroupDeleted } from '../../../../../campaigns-reports/config/predicates/campaignGroupPredicates';
import { useBudgetFormFieldValue } from '../../../Budget/hooks/useBudgetFormFieldValue';
import { useCampaignsGroupBudgetDistributionMethod } from '../../../Budget/hooks/useCampaignsGroupBudgetDistributionMethod';
import { useSpendingLimitOptions } from '../../../Budget/hooks/useSpendingLimitOptions';
import { useCampaignGroupDropdownData } from '../../hooks/useCampaignGroupDropdownData';
import { useFetchedCampaignGroup } from '../../hooks/useFetchedCampaignGroup';
import { CampaignGroupOption } from '../CampaignGroupOption/CampaignGroupOption';
import styles from './CampaignGroup.module.scss';

const PAGE_SIZE = 10;
const validations = [
    {
        validationFn: (value, _, { campaignGroupIsMandatory, isMandatoryHierarchyEnabled }) => {
            return !(campaignGroupIsMandatory || isMandatoryHierarchyEnabled) || Number(value) !== -1;
        },
        messageId: 'campaign.group.placeholder',
        defaultMessage: 'Select a Campaign Group',
    },
];
const DropdownWithIndication = withIndication(PaginatedDropdown);
const invalidValueStyle = {
    control: props => ({ ...props, border: `1px solid ${styles.invalidValueColor}` }),
};
const CampaignGroupIsMandatory = () => useConfigMatch({ [FEATURE_FLAGS.CAMPAIGN_GROUP_IS_MANDATORY]: 'true' });
const useIsMandatoryHierarchyEnabled = () => useConfigMatch({ [FEATURE_FLAGS.IS_MANDATORY_HIERARCHY_ENABLED]: 'true' });
export const CampaignGroup = ({ disabled }) => {
    const isNoneSpendingLimitDisplayed = useSpendingLimitOptions();
    const { onChange: onBudgetChange } = useBudgetFormFieldValue();
    const { mode } = useFormDataContext();
    const [lastCreatedCampaignGroupId] = useLastCreatedCampaignGroupId();
    const campaignGroupIsMandatory = CampaignGroupIsMandatory();
    const isMandatoryHierarchyEnabled = useIsMandatoryHierarchyEnabled();
    const validationDependencies = useMemo(
        () => ({
            campaignGroupIsMandatory,
            isMandatoryHierarchyEnabled,
        }),
        [campaignGroupIsMandatory, isMandatoryHierarchyEnabled]
    );
    const [reportEntityIds] = useReportEntityIds();
    const [reportEntityType] = useReportEntityType();
    const selectedCampaignGroupId = reportEntityType === ENTITY.CAMPAIGNS_GROUP ? reportEntityIds : AllEntityId;
    const { value, onChange, scrollRef, failedValidationData, isDirty } = useCampaignGroupIdFormValidatedValue({
        validations,
        validationDependencies,
        initialCampaignGroupId: lastCreatedCampaignGroupId ?? selectedCampaignGroupId,
        hasInitialData: false,
    });
    const {
        initialData: { campaignGroupId: initialValue },
    } = useFormDataContext();

    const cache = useSelector(campaignsGroupMapSelector);
    useFetchedCampaignGroup(initialValue);

    const selectedValueObject =
        value && Number(value) !== -1
            ? {
                  value,
                  label: get(cache, `${value}.name`) || get(cache, `${value}.campaignsGroupName`),
              }
            : null;
    const { options, setOptions, loadPage } = useCampaignGroupDropdownData();
    const selectedCampaignGroup = get(cache, value);
    const { onBudgetDistributionMethodChange } = useCampaignsGroupBudgetDistributionMethod();
    const isNewForm = mode === FORM_MODES.CREATE || mode === FORM_MODES.DUPLICATE;

    useEffect(() => {
        if (isNewForm && isCampaignGroupDeleted(selectedCampaignGroup)) {
            onChange('');
        }
    }, [selectedCampaignGroup, onChange, isNewForm]);

    useEffect(() => {
        if (isDirty) {
            onBudgetDistributionMethodChange(selectedCampaignGroup?.budgetDistributionMethod);
        }
    }, [isDirty, selectedCampaignGroup?.budgetDistributionMethod, onBudgetDistributionMethodChange]);

    const onReset = () => {
        onChange('-1');
        onBudgetChange(getBudgetInitialValuesWithoutCampaignGroup(isNoneSpendingLimitDisplayed));
    };

    const showInEditCampaign = mode !== FORM_MODES.EDIT || (mode === FORM_MODES.EDIT && !isNil(value));
    return (
        showInEditCampaign && (
            <>
                <DropdownWithIndication
                    id="campaign-group-dropdown"
                    ref={scrollRef}
                    options={options}
                    loadPage={loadPage}
                    onOptionsLoaded={setOptions}
                    pageSize={PAGE_SIZE}
                    onChange={({ value }) => onChange(value)}
                    placeholder={
                        <FormattedMessage id="campaign.group.placeholder" defaultMessage="Select a Campaign Group" />
                    }
                    styles={isEmpty(failedValidationData) ? undefined : invalidValueStyle}
                    disabled={disabled}
                    selectedValueObject={selectedValueObject}
                    DropdownMenuComponent={BareDropdown}
                    optionItemRenderer={CampaignGroupOption}
                    containerClassName={styles['dropdown-container']}
                    className={styles['item-container']}
                    isOptionDisabled={option => isCampaignGroupDeleted(option)}
                    {...failedValidationData}
                />
                {isNewForm && !campaignGroupIsMandatory && !isMandatoryHierarchyEnabled && (
                    <span onClick={onReset} className={styles['reset-button']}>
                        <FormattedMessage id="campaign.groups.reset.button" defaultMessage="Clear Selection" />
                    </span>
                )}
            </>
        )
    );
};
